import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CustomerDetailPanel = ({ customer, onClose, onEdit, onNavigateToBids, onNavigateToProjects }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'projects', label: 'Active Projects', icon: 'Briefcase' },
    { id: 'bids', label: 'Bid History', icon: 'FileText' },
    { id: 'communications', label: 'Communications', icon: 'MessageSquare' }
  ];

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm lg:relative lg:bg-transparent lg:backdrop-blur-none">
      <div className="fixed inset-y-0 right-0 w-full lg:w-[600px] bg-card border-l border-border shadow-2xl overflow-y-auto lg:relative lg:shadow-none">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-card border-b border-border px-4 md:px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Building2" size={24} className="text-accent" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg md:text-xl font-semibold text-foreground truncate capitalize">
                  {customer?.companyName}
                </h2>
                <p className="text-sm text-muted-foreground truncate capitalize">{customer?.contactPerson}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab?.id
                    ? 'bg-accent/10 text-accent' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span className="hidden sm:inline">{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 md:p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Contact Information */}
              <div>
                <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="User" size={18} className="text-accent" />
                  Contact Information
                </h3>
                <div className="space-y-3 bg-muted/30 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="Mail" size={16} className="text-muted-foreground mt-1 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="text-sm text-foreground break-all">{customer?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Icon name="Phone" size={16} className="text-muted-foreground mt-1 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <p className="text-sm text-foreground">{customer?.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Icon name="MapPin" size={16} className="text-muted-foreground mt-1 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-muted-foreground">Address</p>
                      <p className="text-sm text-foreground">{customer?.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Financial Summary */}
              <div>
                <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="DollarSign" size={18} className="text-accent" />
                  Financial Summary
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-1">Total Contract Value</p>
                    <p className="text-xl md:text-2xl font-bold text-accent">
                      {formatCurrency(customer?.totalValue)}
                    </p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-1">Active Projects</p>
                    <p className="text-xl md:text-2xl font-bold text-foreground">
                      {customer?.activeProjects}
                    </p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-1">Bid Conversion Rate</p>
                    <p className="text-xl md:text-2xl font-bold text-success">
                      {customer?.bidConversionRate}%
                    </p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-1">Total Bids</p>
                    <p className="text-xl md:text-2xl font-bold text-foreground">
                      {customer?.totalBids}
                    </p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div>
                <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Zap" size={18} className="text-accent" />
                  Quick Actions
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button
                    variant="outline"
                    onClick={() => onNavigateToBids(customer)}
                    iconName="FilePlus"
                    iconPosition="left"
                    className="justify-start"
                  >
                    Create New Bid
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => onNavigateToProjects(customer)}
                    iconName="FolderPlus"
                    iconPosition="left"
                    className="justify-start"
                  >
                    Create New Project
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => onEdit(customer)}
                    iconName="Pencil"
                    iconPosition="left"
                    className="justify-start"
                  >
                    Edit Customer
                  </Button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'projects' && (
            <div className="space-y-4">
              {customer?.projects?.map((project) => (
                <div key={project?.id} className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0 flex-1">
                      <h4 className="text-base font-semibold text-foreground mb-1 truncate">
                        {project?.name}
                      </h4>
                      <p className="text-sm text-muted-foreground">Project #{project?.number}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${
                      project?.status === 'Active' ?'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                    }`}>
                      {project?.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Start Date</p>
                      <p className="text-sm text-foreground">{formatDate(project?.startDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Project Value</p>
                      <p className="text-sm font-semibold text-accent">
                        {formatCurrency(project?.value)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-background rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-accent h-full transition-all"
                        style={{ width: `${project?.progress}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-foreground whitespace-nowrap">
                      {project?.progress}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'bids' && (
            <div className="space-y-4">
              {customer?.bids?.map((bid) => (
                <div key={bid?.id} className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0 flex-1">
                      <h4 className="text-base font-semibold text-foreground mb-1 truncate">
                        {bid?.projectName}
                      </h4>
                      <p className="text-sm text-muted-foreground">Bid #{bid?.number}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${
                      bid?.status === 'Won' ?'bg-success/10 text-success'
                        : bid?.status === 'Lost' ?'bg-error/10 text-error' :'bg-warning/10 text-warning'
                    }`}>
                      {bid?.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Submitted Date</p>
                      <p className="text-sm text-foreground">{formatDate(bid?.submittedDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Bid Amount</p>
                      <p className="text-sm font-semibold text-accent">
                        {formatCurrency(bid?.amount)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'communications' && (
            <div className="space-y-4">
              {customer?.communications?.map((comm) => (
                <div key={comm?.id} className="bg-muted/30 rounded-lg p-4">
                  <div className="flex items-start gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={comm?.type === 'email' ? 'Mail' : 'Phone'} size={14} className="text-accent" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-foreground">{comm?.subject}</p>
                        <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                          {formatDate(comm?.date)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">{comm?.notes}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailPanel;